import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Checkout() {
  const { user, setCartCount } = useAuth();
  const nav = useNavigate();
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [address, setAddress] = useState({
    full_name: user?.name || "",
    phone: "",
    line1: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [payment, setPayment] = useState("cod");
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/cart").then(({ data }) => setCart(data));
  }, [user]);

  if (!user) { nav("/login"); return null; }

  const upd = (k) => (e) => setAddress({ ...address, [k]: e.target.value });

  const place = async (e) => {
    e.preventDefault();
    if (cart.items.length === 0) { toast.error("Cart is empty"); return; }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders/checkout", { address, payment_method: payment });
      setCartCount(0);
      toast.success("Order placed! Order #" + data.id.slice(-6));
      nav("/orders");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally { setPlacing(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Almost there</div>
      <h1 className="font-display text-3xl md:text-4xl text-[#0F3D2E]">Checkout</h1>

      <form onSubmit={place} className="mt-8 grid lg:grid-cols-[1fr_380px] gap-8" data-testid="checkout-form">
        <div className="space-y-6">
          <div className="rounded-xl border border-[#E2E8E5] bg-white p-6">
            <h2 className="font-display text-xl text-[#0F3D2E] mb-4">Shipping Address</h2>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="text-xs text-[#3B5249]">Full name</label>
                <Input required value={address.full_name} onChange={upd("full_name")} data-testid="addr-name" />
              </div>
              <div>
                <label className="text-xs text-[#3B5249]">Phone</label>
                <Input required value={address.phone} onChange={upd("phone")} data-testid="addr-phone" />
              </div>
              <div>
                <label className="text-xs text-[#3B5249]">Pincode</label>
                <Input required value={address.pincode} onChange={upd("pincode")} data-testid="addr-pincode" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-[#3B5249]">Address line</label>
                <Input required value={address.line1} onChange={upd("line1")} data-testid="addr-line1" />
              </div>
              <div>
                <label className="text-xs text-[#3B5249]">City</label>
                <Input required value={address.city} onChange={upd("city")} data-testid="addr-city" />
              </div>
              <div>
                <label className="text-xs text-[#3B5249]">State</label>
                <Input required value={address.state} onChange={upd("state")} data-testid="addr-state" />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-[#E2E8E5] bg-white p-6">
            <h2 className="font-display text-xl text-[#0F3D2E] mb-4">Payment Method</h2>
            <div className="space-y-2">
              {[
                ["cod", "Cash on Delivery", "Pay when your order arrives"],
                ["mock_online", "Online (Demo)", "Mock payment gateway — no real charge"],
              ].map(([v, t, d]) => (
                <label key={v} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${payment===v ? "border-[#14A085] bg-[#E8F5F2]" : "border-[#E2E8E5]"}`} data-testid={`pay-${v}`}>
                  <input type="radio" name="pay" value={v} checked={payment===v} onChange={() => setPayment(v)} className="mt-1" />
                  <div>
                    <div className="text-sm font-medium text-[#0F3D2E]">{t}</div>
                    <div className="text-xs text-[#6E877C]">{d}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-[#E2E8E5] bg-white p-6 h-fit sticky top-24">
          <h2 className="font-display text-xl text-[#0F3D2E]">Your Order</h2>
          <div className="mt-4 space-y-3 max-h-64 overflow-auto">
            {cart.items.map((it) => (
              <div key={it.id} className="flex gap-3 text-sm">
                <img src={it.images?.[0]} alt="" className="w-12 h-12 rounded object-cover" />
                <div className="flex-1">
                  <div className="text-[#0F3D2E] line-clamp-1">{it.title}</div>
                  <div className="text-xs text-[#6E877C]">Qty {it.qty}</div>
                </div>
                <div className="text-[#0F3D2E]">₹{(it.price*it.qty).toLocaleString()}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 border-t border-[#E2E8E5] pt-3 space-y-2 text-sm">
            <div className="flex justify-between text-[#3B5249]"><span>Subtotal</span><span>₹{cart.subtotal.toLocaleString()}</span></div>
            <div className="flex justify-between text-[#3B5249]"><span>Shipping</span><span className="text-[#14A085]">Free</span></div>
            <div className="flex justify-between font-semibold text-[#0F3D2E] text-base"><span>Total</span><span data-testid="checkout-total">₹{cart.subtotal.toLocaleString()}</span></div>
          </div>
          <Button type="submit" disabled={placing || cart.items.length===0} className="w-full mt-6 rounded-full bg-[#0F3D2E] hover:bg-[#14A085] text-white" data-testid="place-order-btn">
            {placing ? "Placing…" : "Place Order"}
          </Button>
          <p className="text-xs text-[#6E877C] mt-3 text-center">
            By placing your order you agree to Florvanta's Terms.
          </p>
        </div>
      </form>
    </div>
  );
}
